import type { ExpandedState } from "@tanstack/react-table";
import { Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input.tsx";
import { flattenTree, GicsHierarchyTable } from "@/lib/industry";
import type { GicsTreeNode } from "../_components/types.ts";

interface Props {
  sectors: GicsTreeNode[];
}

export default function IndustriesHierarchyView({ sectors }: Props) {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [searchQuery, setSearchQuery] = useState("");

  const isSearching = searchQuery.trim().length > 0;

  const displayData = useMemo(() => {
    if (!searchQuery.trim()) return sectors;

    const query = searchQuery.toLowerCase();
    const flat = flattenTree(sectors);
    return flat
      .filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.code.toLowerCase().includes(query),
      )
      .map((item) => ({
        id: item.id,
        code: item.code,
        name: item.name,
        level: item.level,
      }));
  }, [sectors, searchQuery]);

  return (
    <div className="flex flex-col h-full bg-card rounded-lg border border-border">
      <div className="p-4 border-b border-border">
        <div className="relative w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or code..."
            className="pl-9 pr-9"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <GicsHierarchyTable
        data={displayData}
        expanded={isSearching ? {} : expanded}
        onExpandedChange={setExpanded}
        showChildrenCount
        emptyMessage={
          isSearching ? "No matching industries" : "No industries found"
        }
        className="flex-1"
      />

      <div className="px-4 py-3 border-t border-border">
        <div className="text-sm text-muted-foreground">
          {displayData.length} items
          {isSearching && ` matching "${searchQuery}"`}
          {!isSearching && Object.keys(expanded).length > 0 && (
            <span className="ml-2">
              ({Object.values(expanded).filter(Boolean).length} expanded)
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
